import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Currency, Fee, Order, OrderOverallStatus } from '@hero/schema';
import { isInternalShipment, isOrderFieldEditable } from '@hero/core';
import { useCurrentUser } from '@hero/hooks-shared';
import { useViewerLeg } from '@hero/web-hooks';
import { PlusIcon } from '@hero/web-icons';

import { Price } from '../../../../app/Price';
import { Button } from '../../../../ui/Button';
import { OrderPriceEditDrawer } from '../../Drawer/OrderPriceEditDrawer';
import { OrderChangeClientModal } from '../../OrderActions/OrderChangeClientModal';

export interface PriceCellProps {
  order: Order;
}

/**
 * Strip auto-managed fees (currently `items_insurance`) from the
 * displayed list — the price service re-applies them automatically
 * on every save, and they're not user-edited, so showing them as a
 * line item just confuses the rate the user agreed on.
 */
const HIDDEN_FEE_TYPES = new Set(['items_insurance']);

/**
 * Order-table cell that surfaces the **viewer's** leg pricing and
 * doubles as the entry point into `OrderPriceEditDrawer`.
 *
 * Walk-in / no-client orders have no commercial leg yet. For the
 * shipper we show **Add price**, which opens the client selector
 * first — attaching a client seeds `contracts[0]`, then we open the
 * price drawer. Tenants who can *see* an order without sitting on a
 * leg still get an em dash (dead-end drawer avoided).
 */
export const PriceCell: React.FC<PriceCellProps> = ({ order }) => {
  const { t } = useTranslation(['m-order', 'c-price']);
  const leg = useViewerLeg(order);
  const { orgId, user } = useCurrentUser();
  const [isPriceOpen, setIsPriceOpen] = useState(false);
  const [isClientOpen, setIsClientOpen] = useState(false);
  const isInternal = isInternalShipment(order);

  const isShipperViewer = useMemo(() => {
    const shipperId = order.shipper?._id?.toString();
    if (!shipperId) return false;
    if (orgId && orgId.toString() === shipperId) return true;
    const userId = (user as { _id?: { toString(): string } } | undefined)?._id?.toString?.();
    return Boolean(userId && userId === shipperId);
  }, [order.shipper?._id, orgId, user]);

  const statusEditable = isOrderFieldEditable(
    'carrier_price',
    (order.status?.order || 'new') as OrderOverallStatus,
  );

  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const openPrice = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsPriceOpen(true);
  };
  const openClientThenPrice = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsClientOpen(true);
  };

  const loadPrice = leg?.amount?.itemsOriginalSubtotal ?? 0;
  const currency: Currency = (leg?.amount?.currency ?? 'SAR') as Currency;

  const visibleFees: Fee[] = (leg?.amount?.fees?.applied ?? []).filter(
    f => !HIDDEN_FEE_TYPES.has(f.type),
  );
  const feesTotal = visibleFees.reduce((acc, f) => acc + (f.amount || 0), 0);

  const needsClientFirst = isInternal && isShipperViewer && statusEditable;
  const showAddPrice = Boolean(leg) && loadPrice === 0 && !isInternal;

  if (needsClientFirst) {
    return (
      <>
        <div onClick={stop}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={openClientThenPrice}
            className="h-auto px-2 py-1 -ms-2 text-primary">
            <PlusIcon className="w-4 h-4 me-1" />
            {t('m-order:table.price_cell.add_price')}
          </Button>
        </div>
        <OrderChangeClientModal
          order={order}
          isOpen={isClientOpen}
          purpose="before_price"
          onClose={() => setIsClientOpen(false)}
          onAssigned={() => setIsPriceOpen(true)}
        />
        <OrderPriceEditDrawer
          order={order}
          isOpen={isPriceOpen}
          onClose={() => setIsPriceOpen(false)}
        />
      </>
    );
  }

  if (!leg || isInternal) {
    return (
      <span className="text-sm text-muted-foreground" onClick={stop}>
        —
      </span>
    );
  }

  if (showAddPrice) {
    return (
      <>
        <div onClick={stop}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={openPrice}
            className="h-auto px-2 py-1 -ms-2 text-primary">
            <PlusIcon className="w-4 h-4 me-1" />
            {t('m-order:table.price_cell.add_price')}
          </Button>
        </div>
        <OrderPriceEditDrawer
          order={order}
          isOpen={isPriceOpen}
          onClose={() => setIsPriceOpen(false)}
        />
      </>
    );
  }

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => openPrice()}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPrice();
          }
        }}
        className="flex flex-col gap-0.5 cursor-pointer hover:underline underline-offset-4 decoration-dotted">
        <Price
          amount={loadPrice}
          currency={currency}
          className="text-sm font-semibold text-gray-900"
        />
        {feesTotal > 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <span>{t('m-order:table.price_cell.fees')}:</span>
            <Price amount={feesTotal} currency={currency} size="xs" />
          </div>
        )}
      </div>
      <OrderPriceEditDrawer
        order={order}
        isOpen={isPriceOpen}
        onClose={() => setIsPriceOpen(false)}
      />
    </>
  );
};
